"use client";

import React, { useState, useEffect } from "react";
import { Search } from "lucide-react";
import filterIcon from "../../assets/images/trading/filter.svg";
import sortIcon from "../../assets/images/trading/sort.svg";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { useDposSessions } from "../../hooks/query/dposQueries";
import { resolveDposSessionStatus } from "./dposPaymentSessionUtils";
import { type DposSessionStatus } from "../../services/dposService";
import { formatRandsDisplay } from "../../utils/zarDisplayFormat";

interface DPOSPaymentHistoryProps {
  businessId: string;
  isPageLoading?: boolean;
  refreshSignal?: number;
}

const PendingIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <circle cx="7" cy="7" r="6" stroke="#F57C00" strokeWidth="1.5" />
    <circle cx="7" cy="7" r="2.5" fill="#F57C00" />
    <line
      x1="7"
      y1="1"
      x2="7"
      y2="3"
      stroke="#F57C00"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <line
      x1="7"
      y1="11"
      x2="7"
      y2="13"
      stroke="#F57C00"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <line
      x1="1"
      y1="7"
      x2="3"
      y2="7"
      stroke="#F57C00"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <line
      x1="11"
      y1="7"
      x2="13"
      y2="7"
      stroke="#F57C00"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const PaidIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <circle cx="7" cy="7" r="7" fill="#17C3A5" />
    <path
      d="M4.5 7L6.2 8.7L9.5 5.5"
      stroke="white"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ExpiredIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <circle cx="7" cy="7" r="6" stroke="#9CA3AF" strokeWidth="1.5" />
    <line
      x1="7"
      y1="3.5"
      x2="7"
      y2="7.5"
      stroke="#9CA3AF"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <circle cx="7" cy="9.5" r="0.75" fill="#9CA3AF" />
  </svg>
);

const CancelledIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <circle cx="7" cy="7" r="6" stroke="#EB3366" strokeWidth="1.5" />
    <line
      x1="4.5"
      y1="4.5"
      x2="9.5"
      y2="9.5"
      stroke="#EB3366"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <line
      x1="9.5"
      y1="4.5"
      x2="4.5"
      y2="9.5"
      stroke="#EB3366"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

function getStatusBadgeConfig(status: DposSessionStatus | string) {
  const resolvedStatus = resolveDposSessionStatus(status);
  switch (resolvedStatus) {
    case "pending":
      return {
        icon: <PendingIcon />,
        label: "Pending",
        bg: "#FFF3E0",
        color: "#F57C00",
      };
    case "processing":
      return {
        icon: <PendingIcon />,
        label: "Processing",
        bg: "#E8F4FF",
        color: "#118AB2",
      };
    case "paid":
      return {
        icon: <PaidIcon />,
        label: "Paid",
        bg: "#E8F9F6",
        color: "#17C3A5",
      };
    case "confirmed":
      return {
        icon: <PaidIcon />,
        label: "Confirmed",
        bg: "#E8F9F6",
        color: "#17C3A5",
      };
    case "expired":
      return {
        icon: <ExpiredIcon />,
        label: "Expired",
        bg: "#F5F4F7",
        color: "#9CA3AF",
      };
    case "cancelled":
      return {
        icon: <CancelledIcon />,
        label: "Cancelled",
        bg: "#FFF0F3",
        color: "#EB3366",
      };
    case "failed":
      return {
        icon: <CancelledIcon />,
        label: "Failed",
        bg: "#FFF0F3",
        color: "#EB3366",
      };
    case "active":
    default:
      return {
        icon: <PendingIcon />,
        label: "Active",
        bg: "#FFF3E0",
        color: "#F57C00",
      };
  }
}

function StatusBadge({ status }: { status: DposSessionStatus | string }) {
  const cfg = getStatusBadgeConfig(status);
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 whitespace-nowrap"
      style={{ backgroundColor: cfg.bg }}
    >
      {cfg.icon}
      <span
        className="text-[11px] font-bold uppercase"
        style={{ color: cfg.color }}
      >
        {cfg.label}
      </span>
    </span>
  );
}

function Cell({ label, value }: { label: string; value: string }) {
  return (
    <td className="py-4 pr-6 align-top">
      <p className="text-[11px] text-[#9CA3AF] mb-1">{label}</p>
      <p className="text-[14px] font-semibold text-[#33125F]">{value}</p>
    </td>
  );
}

function formatDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleDateString("en-ZA", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
}

function formatAmount(amount: string, currency?: string | null): string {
  const num = parseFloat(amount);
  if (isNaN(num)) return amount;
  const c = (currency || "ZAR").trim().toUpperCase();
  if (c === "ZAR" || c === "R") {
    return formatRandsDisplay(num);
  }
  return `${c} ${num.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function shortId(id: string): string {
  return `#${String(id).slice(-8).toUpperCase()}`;
}

export default function DPOSPaymentHistory({
  businessId,
  isPageLoading = false,
  refreshSignal = 0,
}: DPOSPaymentHistoryProps) {
  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [sortBy, setSortBy] = useState("date-desc");
  const [page, setPage] = useState(1);

  const isFilterActive = filterStatus !== "all";
  const isSortActive = sortBy !== "date-desc";

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounced(search.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const {
    data,
    isLoading: isSessionsLoading,
    refetch,
  } = useDposSessions(businessId || null, {
    page,
    limit: 6,
    status: filterStatus !== "all" ? filterStatus : undefined,
    sort: sortBy,
    search: searchDebounced || undefined,
  });

  useEffect(() => {
    if (refreshSignal > 0) refetch();
  }, [refreshSignal, refetch]);

  const isLoading = isPageLoading || isSessionsLoading;

  const sessions = (data?.results || []) as Array<{
    id: string;
    amount: string;
    asset?: string | null;
    currency?: string | null;
    status: DposSessionStatus | string;
    createdAt: string;
    paidAt: string | null;
  }>;
  const pageLimit = data?.limit || 6;
  const rangeStart = data?.totalItems ? (page - 1) * pageLimit + 1 : 0;
  const rangeEnd = data?.totalItems
    ? Math.min((page - 1) * pageLimit + sessions.length, data.totalItems)
    : 0;

  return (
    <div className="bg-white border border-[#EBE9EF] rounded-2xl p-6 w-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <h3 className="text-[20px] font-semibold text-[#33125F]">
          Digital POS payment history
        </h3>
        <div className="flex items-center gap-3">
          <div className="bg-[rgba(235,233,239,0.4)] border border-[#EBE9EF] rounded-full h-[44px] flex items-center pl-4 pr-4 gap-2 min-w-[180px]">
            <Search size={13} className="text-[#6D6584] shrink-0" />
            <input
              type="text"
              placeholder="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="bg-transparent border-none outline-none text-[13px] text-[#6D6584] placeholder-[#9CA3AF] w-full"
            />
          </div>

          {/* Filter dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className={`h-[44px] flex items-center justify-center bg-white border rounded-full text-[13px] font-semibold text-[#6D6584] hover:bg-gray-50 transition-colors gap-2 px-4 ${
                  isFilterActive
                    ? "border-[#EB3366] bg-[#EB3366]/5"
                    : "border-[#EBE9EF]"
                }`}
              >
                <span>Filter</span>
                <img
                  src={filterIcon}
                  alt="filter"
                  className="w-[14px] h-[14px]"
                />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuLabel>Filter by Status</DropdownMenuLabel>
              <DropdownMenuRadioGroup
                value={filterStatus}
                onValueChange={(v) => {
                  setFilterStatus(v);
                  setPage(1);
                }}
              >
                <DropdownMenuRadioItem value="all">
                  All Statuses
                </DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="active">
                  Active
                </DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="paid">Paid</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="confirmed">
                  Confirmed
                </DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="expired">
                  Expired
                </DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="cancelled">
                  Cancelled
                </DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
              {isFilterActive && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => {
                      setFilterStatus("all");
                      setPage(1);
                    }}
                    className="text-red-600 focus:text-red-600"
                  >
                    Clear Filters
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Sort dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className={`h-[44px] flex items-center justify-center bg-white border rounded-full text-[13px] font-semibold text-[#6D6584] hover:bg-gray-50 transition-colors gap-2 px-4 ${
                  isSortActive
                    ? "border-[#EB3366] bg-[#EB3366]/5"
                    : "border-[#EBE9EF]"
                }`}
              >
                <span>Sort</span>
                <img src={sortIcon} alt="sort" className="w-[14px] h-[14px]" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuLabel>Sort by</DropdownMenuLabel>
              <DropdownMenuRadioGroup
                value={sortBy}
                onValueChange={(v) => {
                  setSortBy(v);
                  setPage(1);
                }}
              >
                <DropdownMenuRadioItem value="date-desc">
                  Date (Newest First)
                </DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="date-asc">
                  Date (Oldest First)
                </DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="amount-desc">
                  Amount (Highest First)
                </DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="amount-asc">
                  Amount (Lowest First)
                </DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <tbody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i} className="border-b border-[#F5F4F7]">
                  {Array.from({ length: 5 }).map((__, j) => (
                    <td key={j} className="py-4 pr-6 align-top">
                      <div className="h-3 w-16 bg-gray-200 rounded mb-2 animate-pulse" />
                      <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
                    </td>
                  ))}
                  <td className="py-4 align-middle">
                    <div className="h-7 w-20 bg-gray-200 rounded-full animate-pulse" />
                  </td>
                </tr>
              ))
            ) : sessions.length === 0 ? (
              <tr>
                <td
                  colSpan={6}
                  className="text-center py-10 text-[14px] text-[#6D6584]"
                >
                  {searchDebounced
                    ? "No sessions match your search."
                    : "No payment sessions found."}
                </td>
              </tr>
            ) : (
              sessions.map((session) => (
                <tr
                  key={session.id}
                  className="border-b border-[#F5F4F7] last:border-0"
                >
                  <Cell label="Session ID" value={shortId(session.id)} />
                  <Cell
                    label="Amount"
                    value={formatAmount(
                      session.amount,
                      session.currency || session.asset
                    )}
                  />
                  <Cell
                    label="Currency"
                    value={session.currency || session.asset || "ZAR"}
                  />
                  <Cell label="Created" value={formatDate(session.createdAt)} />
                  <Cell
                    label="Paid at"
                    value={session.paidAt ? formatDate(session.paidAt) : "—"}
                  />
                  <td className="py-4 align-middle">
                    <StatusBadge status={session.status} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {!isLoading && data && data.totalItems > 0 && (
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#F5F4F7]">
          <span className="text-[13px] text-[#9CA3AF]">
            {rangeStart}–{rangeEnd} of {data.totalItems} sessions
          </span>
          <div className="flex gap-2">
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              className="h-8 px-3 rounded-full border border-[#EBE9EF] text-[13px] text-[#6D6584] disabled:opacity-40 hover:bg-[#F5F4F7] transition-colors"
            >
              Previous
            </button>
            <span className="h-8 px-3 flex items-center text-[13px] text-[#33125F] font-semibold">
              {page} / {data.totalPages || 1}
            </span>
            <button
              disabled={page >= (data.totalPages || 1)}
              onClick={() => setPage((p) => p + 1)}
              className="h-8 px-3 rounded-full border border-[#EBE9EF] text-[13px] text-[#6D6584] disabled:opacity-40 hover:bg-[#F5F4F7] transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
